import React, { useEffect } from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
    Textarea,
} from "@/ui";
import { Icon } from "@/components";
import { useForm, useFormActions, useMeta, useValidator } from "@/hooks";
import {
    MUTATION_UPDATE_KLAVIYO,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import omit from "lodash/omit";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const KlaviyoForm = ({ app }) => {
    const { user } = useMeta();

    const { inputs, setInputs, inputChange } = useForm();

    useEffect(() => {
        const lists = app.lists
            ? app.lists.map(list => omit(list, "__typename"))
            : [];

        setInputs({
            private_key: app.private_key,
            lists: JSON.stringify(lists, null, 2),
            used_for_subscription: app.used_for_subscription,
        });
    }, [app]);

    const getLists = () => {
        try {
            return JSON.parse(inputs?.lists);
        } catch (error) {
            return [];
        }
    };

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...app,
                ...inputs,
                lists: getLists(),
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "private_key",
                method: "isEmpty",
                validWhen: false,
                message: "Private is required.",
            },
            {
                field: "lists",
                method: "isJSON",
                validWhen: true,
                args: [],
                message: "Invalid Lists structure.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Klavio",
        "",
        MUTATION_UPDATE_KLAVIYO,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
    );

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Klavio</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Klavio Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Klavio actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section>
                        <Switch
                            checked={Boolean(inputs.used_for_subscription)}
                            label="Used for subscription"
                            name="used_for_subscription"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={1} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, "private_key")}
                                fullWidth
                                label="Private Key"
                                name="private_key"
                                onChange={inputChange}
                                value={inputs.private_key ?? ""}
                            />
                            <Textarea
                                error={fieldError(validator, "lists")}
                                fullWidth
                                label="Lists"
                                name="lists"
                                onChange={inputChange}
                                rows={13}
                                value={inputs?.lists || "[]"}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default KlaviyoForm;
